// src/utils/permissionHelpers.ts - Helper functions pour les permissions CRUD granulaires

import { RolePermissions } from '../types/user';

export interface CRUDPermissions {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

/**
 * Récupère les permissions CRUD pour une ressource donnée
 * Utilise uniquement les permissions granulaires (canView, canCreate, canUpdate, canDelete)
 */
const getCRUDPermissionsForResource = (
  permissions: RolePermissions | null | undefined,
  resourceName: string,
  canViewKey: keyof RolePermissions,
  canCreateKey?: keyof RolePermissions,
  canUpdateKey?: keyof RolePermissions,
  canDeleteKey?: keyof RolePermissions
): CRUDPermissions => {
  if (!permissions) {
    return { canView: false, canCreate: false, canUpdate: false, canDelete: false };
  }
  
  // Utiliser uniquement les permissions granulaires
  const canView = permissions[canViewKey] as boolean | undefined;
  const canCreate = canCreateKey ? (permissions[canCreateKey] as boolean | undefined) : false;
  const canUpdate = canUpdateKey ? (permissions[canUpdateKey] as boolean | undefined) : false;
  const canDelete = canDeleteKey ? (permissions[canDeleteKey] as boolean | undefined) : false;
  
  return {
    canView: canView ?? false,
    canCreate: canCreate ?? false,
    canUpdate: canUpdate ?? false,
    canDelete: canDelete ?? false,
  };
};

/**
 * Permissions pour Products (feature Stock pilotée par canViewProducts)
 */
export const getProductPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Products',
    'canViewProducts',
    'canCreateProducts',
    'canUpdateProducts',
    'canDeleteProducts'
  );
};

/**
 * Permissions pour Clients
 */
export const getClientPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Clients',
    'canViewClients',
    'canCreateClients',
    'canUpdateClients',
    'canDeleteClients'
  );
};

/**
 * Permissions pour Services
 */
export const getServicePermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Services',
    'canViewServices',
    'canCreateServices',
    'canUpdateServices',
    'canDeleteServices'
  );
};

/**
 * Permissions pour Ordres de fabrication
 */
export const getManufacturingOrderPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ManufacturingOrders',
    'canViewManufacturingOrders',
    'canCreateManufacturingOrders',
    'canUpdateManufacturingOrders',
    'canDeleteManufacturingOrders'
  );
};

/**
 * Permissions pour Employees
 */
export const getEmployeePermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Employees',
    'canViewEmployees',
    'canCreateEmployees',
    'canUpdateEmployees',
    'canDeleteEmployees'
  );
};

/**
 * Permissions pour Referents
 */
export const getReferentPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Referents',
    'canViewReferents',
    'canCreateReferents',
    'canUpdateReferents',
    'canDeleteReferents'
  );
};

/**
 * Permissions pour Categories
 */
export const getCategoryPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Categories',
    'canViewCategories',
    'canCreateCategories',
    'canUpdateCategories',
    'canDeleteCategories'
  );
};

/**
 * Permissions pour Suppliers
 */
export const getSupplierPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Suppliers',
    'canViewSuppliers',
    'canCreateSuppliers',
    'canUpdateSuppliers',
    'canDeleteSuppliers'
  );
};

/**
 * Permissions pour Deliveries
 */
export const getDeliveryPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Deliveries',
    'canViewDeliveries',
    'canCreateDeliveries',
    'canUpdateDeliveries',
    'canDeleteDeliveries'
  );
};

/**
 * Permissions pour Appointments
 */
export const getAppointmentPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Appointments',
    'canViewAppointments',
    'canCreateAppointments',
    'canUpdateAppointments',
    'canDeleteAppointments'
  );
};

/**
 * Permissions pour Marketing
 */
export const getMarketingPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Marketing',
    'canViewMarketing',
    'canCreateMarketing',
    'canUpdateMarketing',
    'canDeleteMarketing'
  );
};

export interface MessagesClientsPermissions {
  canView: boolean;
  canSend: boolean;
  canUpdate: boolean;
  canManageSettings: boolean;
}

export const getMessagesClientsPermissions = (
  permissions: RolePermissions | null | undefined
): MessagesClientsPermissions => {
  if (!permissions) {
    return { canView: false, canSend: false, canUpdate: false, canManageSettings: false };
  }
  return {
    canView: permissions.canViewMessagesClients ?? false,
    canSend: permissions.canSendMessagesClients ?? false,
    canUpdate: permissions.canUpdateMessagesClients ?? false,
    canManageSettings: permissions.canManageMessagesClientsSettings ?? false,
  };
};

/**
 * Permissions pour Prestations
 */
export const getPrestationPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Prestations',
    'canViewPrestations',
    'canCreatePrestations',
    'canUpdatePrestations',
    'canDeletePrestations'
  );
};

/**
 * Permissions pour StockMovements
 */
export const getStockMovementPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'StockMovements',
    'canViewStockMovements',
    'canCreateStockMovements',
    'canUpdateStockMovements',
    'canDeleteStockMovements'
  );
};

/**
 * Permissions pour ExternalDispatches (bons d'envoi externes)
 */
export const getExternalDispatchPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ExternalDispatches',
    'canViewExternalDispatches',
    'canCreateExternalDispatches',
    'canUpdateExternalDispatches',
    'canDeleteExternalDispatches'
  );
};

/**
 * Permissions pour ExternalDestinations (référentiel boutiques externes)
 */
export const getExternalDestinationPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ExternalDestinations',
    'canViewExternalDestinations',
    'canCreateExternalDestinations',
    'canUpdateExternalDestinations',
    'canDeleteExternalDestinations'
  );
};

/**
 * Permissions pour Payrolls
 */
export const getPayrollPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Payrolls',
    'canViewPayrolls',
    'canCreatePayrolls',
    'canUpdatePayrolls',
    'canDeletePayrolls'
  );
};

/**
 * Permissions pour SalaryAdjustments
 * Note: canDelete utilise canApproveSalaryAdjustments car c'est pour rejeter/approuver les ajustements
 */
export const getSalaryAdjustmentPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  if (!permissions) {
    return { canView: false, canCreate: false, canUpdate: false, canDelete: false };
  }

  const canView = permissions.canViewSalaryAdjustments ?? false;
  const canCreate = permissions.canCreateSalaryAdjustments ?? false;
  const canUpdate = permissions.canUpdateSalaryAdjustments ?? false;
  const canDelete = permissions.canApproveSalaryAdjustments ?? false; // Rejeter/approuver

  return {
    canView: canView ?? false,
    canCreate: canCreate ?? false,
    canUpdate: canUpdate ?? false,
    canDelete: canDelete ?? false,
  };
};

/**
 * Permissions pour Expenses
 */
export const getExpensePermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Expenses',
    'canViewExpenses',
    'canCreateExpenses',
    'canUpdateExpenses',
    'canDeleteExpenses'
  );
};

/**
 * Permissions Types de clients
 */
export const getClientTypesPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ClientTypes',
    'canViewClientTypes',
    'canCreateClientTypes',
    'canUpdateClientTypes',
    'canDeleteClientTypes'
  );
};

/**
 * Permissions Types d'activité
 */
export const getActivityTypesPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ActivityTypes',
    'canViewActivityTypes',
    'canCreateActivityTypes',
    'canUpdateActivityTypes',
    'canDeleteActivityTypes'
  );
};

/**
 * Permissions Catégories d'opportunités
 */
export const getOpportunityCategoriesPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'OpportunityCategories',
    'canViewOpportunityCategories',
    'canCreateOpportunityCategories',
    'canUpdateOpportunityCategories',
    'canDeleteOpportunityCategories'
  );
};

/**
 * Permissions Types / catégories de dépenses
 */
export const getExpenseCategoriesPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ExpenseCategories',
    'canViewExpenseCategories',
    'canCreateExpenseCategories',
    'canUpdateExpenseCategories',
    'canDeleteExpenseCategories'
  );
};

/**
 * Permissions pour InventoryAudit
 */
export const getInventoryAuditPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'InventoryAudit',
    'canViewInventoryAudit',
    'canCreateInventoryAudit',
    'canUpdateInventoryAudit',
    'canDeleteInventoryAudit'
  );
};

/**
 * Permissions pour RoleProfiles (dissociées des permissions Employés)
 */
export const getRoleProfilePermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'RoleProfiles',
    'canViewRoleProfiles',
    'canCreateRoleProfiles',
    'canUpdateRoleProfiles',
    'canDeleteRoleProfiles'
  );
};

/**
 * Permissions pour ProductSales (Ventes de produits)
 */
export const getProductSalePermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'ProductSales',
    'canViewProductSales',
    'canCreateProductSales',
    'canUpdateProductSales',
    'canDeleteProductSales'
  );
};

/**
 * Permissions pour PaymentPlans (plans de paiement / échéances)
 * Remarque : les versements (payments) utiliseront canAddPaymentPlanPayments directement
 */
export const getPaymentPlanPermissions = (
  permissions: RolePermissions | null | undefined
): CRUDPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'PaymentPlans',
    'canViewPaymentPlans',
    'canCreatePaymentPlans',
    'canUpdatePaymentPlans',
    'canCancelPaymentPlans'
  );
};

/** Permissions pour les rapports d'activité (vue, création, voir tous) */
export interface ActivityReportPermissions {
  canView: boolean;
  canCreate: boolean;
  canViewAll: boolean;
}

/**
 * Permissions pour les rapports d'activité
 */
export const getActivityReportPermissions = (
  permissions: RolePermissions | null | undefined
): ActivityReportPermissions => {
  if (!permissions) {
    return { canView: false, canCreate: false, canViewAll: false };
  }
  return {
    canView: permissions.canViewActivityReports ?? false,
    canCreate: permissions.canCreateActivityReports ?? false,
    canViewAll: permissions.canViewAllActivityReports ?? false,
  };
};

/** Permissions pour la configuration des pipelines (Paramètres > Pipeline) */
export type PipelinePermissions = CRUDPermissions;

/**
 * Permissions pour la configuration des pipelines (étapes par business)
 */
export const getPipelinePermissions = (
  permissions: RolePermissions | null | undefined
): PipelinePermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Pipelines',
    'canViewPipelines',
    'canCreatePipelines',
    'canUpdatePipelines',
    'canDeletePipelines'
  );
};

/** Permissions pour les opportunités (Activité > Opportunités) */
export type OpportunitiesPermissions = CRUDPermissions;

/**
 * Permissions pour la page Opportunités (pipeline commercial, deals)
 */
export const getOpportunitiesPermissions = (
  permissions: RolePermissions | null | undefined
): OpportunitiesPermissions => {
  return getCRUDPermissionsForResource(
    permissions,
    'Opportunities',
    'canViewSalesPipeline',
    'canCreateOpportunities',
    'canUpdateOpportunities',
    'canDeleteOpportunities'
  );
};

/** Permissions correction / annulation de ventes (Caisse / pos). */
export type SaleCorrectionPermissions = {
  canVoid: boolean;
  canCorrect: boolean;
  canIssueCredit: boolean;
};

export const getSaleCorrectionPermissions = (
  permissions: RolePermissions | null | undefined
): SaleCorrectionPermissions => ({
  canVoid: permissions?.canVoidSales === true,
  canCorrect: permissions?.canCorrectSales === true,
  canIssueCredit: permissions?.canIssueSaleCredits === true,
});

/** Permissions correction / annulation de versements (paiement en plusieurs). */
export type PaymentPlanPaymentCorrectionPermissions = {
  canVoid: boolean;
  canCorrect: boolean;
};

export const getPaymentPlanPaymentCorrectionPermissions = (
  permissions: RolePermissions | null | undefined
): PaymentPlanPaymentCorrectionPermissions => ({
  canVoid: permissions?.canVoidPaymentPlanPayments === true,
  canCorrect: permissions?.canCorrectPaymentPlanPayments === true,
});

